import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAppState } from '../context/useAppState';
import { getExRanges, moisShort, exerciceLabel, formatCurrency } from '../lib/formatters';
import { getChargeMonthValue } from '../lib/calcEngine';
import { CHARGE_CATS } from '../types';
import type { AppState, Charge, FreelanceCharge } from '../types';
import { cn } from '../lib/utils';

const CAT_META = Object.fromEntries(CHARGE_CATS.map((c) => [c.key, c])) as Record<
  string,
  { key: string; label: string; iconName: string }
>;

export function ChargeMonthlyPage() {
  const { catKey, chargeId } = useParams<{ catKey: string; chargeId: string }>();
  const { state, dispatch } = useAppState();

  const cat = catKey as keyof AppState['charges'];
  const catMeta = CAT_META[catKey ?? ''];

  const charge = useMemo(
    () => (state.charges[cat] as (Charge | FreelanceCharge)[]).find((c) => c.id === chargeId) ?? null,
    [state.charges, cat, chargeId],
  );

  const exRanges = useMemo(
    () => getExRanges(state.meta.exercice1Duree, state.meta.exercice2Duree, state.meta.exercice3Duree),
    [state.meta.exercice1Duree, state.meta.exercice2Duree, state.meta.exercice3Duree],
  );
  const totalMi = exRanges[exRanges.length - 1][1] + 1;

  if (!charge || !catMeta) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Charge introuvable.{' '}
        <Link to="/charges" className="ml-2 underline text-primary">
          Retour aux charges
        </Link>
      </div>
    );
  }

  const isFreelance = cat === 'freelances';
  const title = isFreelance ? (charge as FreelanceCharge).nom : (charge as Charge).libelle;
  const isVariable = !!(charge.variableRevenuId && charge.variablePct > 0);
  const isEditable = !isVariable && charge.mode === 'monthly';
  const isUniform = charge.mode === 'uniform' && !isVariable;

  const [switching, setSwitching] = useState(false);

  function switchToMonthly() {
    setSwitching(true);
    const uniformVal = charge!.montantUniforme ?? 0;
    const filledMonths = Array.from({ length: totalMi }, () => uniformVal);
    dispatch({
      type: 'CHARGE_UPDATE',
      cat,
      id: charge!.id,
      payload: { mode: 'monthly', montantsMensuels: filledMonths },
    });
    setSwitching(false);
  }

  const displayValues = Array.from({ length: totalMi }, (_, mi) =>
    getChargeMonthValue(charge, mi, state.revenus, state.charges),
  );

  function handleMonthChange(mi: number, value: number) {
    dispatch({ type: 'CHARGE_MONTH_UPDATE', cat, id: charge!.id, mi, value });
  }

  function handleReelChange(mi: number, value: number | null) {
    dispatch({ type: 'CHARGE_REEL_UPDATE', cat, id: charge!.id, mi, value });
  }

  const reelArr = charge.montantsReels ?? Array(totalMi).fill(null);

  const grandTotal = displayValues.reduce((s, v) => s + v, 0);
  const exTotals = exRanges.map(([start, end]) =>
    displayValues.slice(start, end + 1).reduce((s, v) => s + v, 0),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link
          to="/charges"
          className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon icon="lucide:arrow-left" className="h-4 w-4" />
          Charges
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/60 rounded px-2 py-0.5">
              <Icon icon={catMeta.iconName} className="h-3.5 w-3.5" />
              {catMeta.label}
            </span>
            {isVariable && (
              <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5">
                Variable — {charge.variablePct}% de{' '}
                {state.revenus.find((r) => r.id === charge.variableRevenuId)?.libelle ?? '?'}
              </span>
            )}
            {isUniform && (
              <button
                onClick={switchToMonthly}
                disabled={switching}
                className="inline-flex items-center gap-1.5 text-xs text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded px-2 py-0.5 transition-colors cursor-pointer"
                title="Basculer en mode mensuel pour modifier chaque mois indépendamment"
              >
                <Icon icon="lucide:table-2" className="h-3.5 w-3.5" />
                Uniforme — cliquer pour modifier mois par mois
              </button>
            )}
          </div>
          <h1 className="text-xl font-semibold mt-1 truncate">{title}</h1>
        </div>

        {/* Totaux rapides */}
        <div className="hidden sm:flex items-center gap-4 text-right shrink-0">
          {exTotals.map((t, ex) => (
            <div key={ex} className="min-w-[80px]">
              <p className="text-[10px] text-muted-foreground">{exerciceLabel(ex, exRanges)}</p>
              <p className="text-sm font-semibold tabular-nums">{formatCurrency(t)}</p>
            </div>
          ))}
          <div className="min-w-[80px] pl-4 border-l border-border">
            <p className="text-[10px] text-muted-foreground">Total</p>
            <p className="text-base font-bold tabular-nums">{formatCurrency(grandTotal)}</p>
          </div>
        </div>
      </div>

      {/* Bannière mode uniforme */}
      {isUniform && (
        <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800">
          <Icon icon="lucide:info" className="h-4 w-4 shrink-0 text-sky-600" />
          <span className="flex-1">
            Cette charge est en mode <strong>Uniforme</strong> ({formatCurrency(charge.montantUniforme ?? 0)}/mois).
            Pour modifier chaque mois indépendamment, basculez en mode mensuel.
          </span>
          <button
            onClick={switchToMonthly}
            disabled={switching}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors disabled:opacity-50"
          >
            {switching ? (
              <Icon icon="lucide:loader-2" className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Icon icon="lucide:table-2" className="h-3.5 w-3.5" />
            )}
            Modifier mois par mois
          </button>
        </div>
      )}

      {/* Légende */}
      {isEditable && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-input bg-white inline-block" />
            Prévu
          </span>
          <span className="text-border">·</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-dashed border-gray-300 bg-gray-50 inline-block" />
            Réel (laisser vide si non connu)
          </span>
        </div>
      )}

      {/* Tables par exercice */}
      <div className="space-y-4">
        {exRanges.map(([exStart, exEnd], ex) => {
          const months = Array.from({ length: exEnd - exStart + 1 }, (_, j) => exStart + j);
          const exTotal = exTotals[ex];

          return (
            <div key={ex} className="rounded-lg border border-border bg-white w-full min-w-0">
              {/* Header exercice */}
              <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-foreground">
                    {exerciceLabel(ex, exRanges)}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {moisShort(exStart)} → {moisShort(exEnd)}
                    {months.length !== 12 ? ` · ${months.length} mois` : ''}
                  </span>
                </div>
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  {formatCurrency(exTotal)}
                </span>
              </div>

              {/* Table */}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
                <div className="overflow-x-auto">
                  <table
                    className="text-xs border-collapse"
                    style={{ minWidth: `${Math.max(months.length * 80 + 80, 300)}px`, width: '100%' }}
                  >
                    <thead>
                      <tr className="bg-[#E5EBE1] [&_th]:!text-[#1F2A20]">
                        <th className="w-16 px-3 py-2 text-left font-medium bg-[#E5EBE1] border-r border-[#D4DCCE] sticky left-0 z-10" />
                        {months.map((mi) => (
                          <th
                            key={mi}
                            className="px-1.5 py-2 text-center font-medium whitespace-nowrap min-w-[76px]"
                          >
                            {moisShort(mi)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Ligne Prévu */}
                      <tr className="border-t border-border/60">
                        <td className="px-3 py-2 text-muted-foreground font-medium bg-muted/20 border-r border-border sticky left-0 z-10 whitespace-nowrap">
                          Prévu
                        </td>
                        {months.map((mi) => {
                          const val = displayValues[mi] ?? 0;
                          return (
                            <td key={mi} className="px-1.5 py-1.5">
                              {isEditable ? (
                                <div className="relative">
                                  <input
                                    type="number"
                                    value={val || ''}
                                    onChange={(e) => handleMonthChange(mi, parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                    className={cn(
                                      'h-7 w-full text-xs text-right pl-1 pr-5 border border-input rounded bg-white focus:outline-none focus:ring-1 focus:ring-ring tabular-nums transition-colors',
                                      val > 0 && 'border-primary/30 bg-primary/5 font-medium',
                                    )}
                                  />
                                  <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">€</span>
                                </div>
                              ) : isUniform ? (
                                <button
                                  onClick={switchToMonthly}
                                  title="Cliquer pour passer en mode mensuel"
                                  className={cn(
                                    'h-7 w-full flex items-center justify-end pr-5 text-right tabular-nums rounded border border-dashed border-sky-200 hover:border-sky-400 hover:bg-sky-50 transition-colors cursor-pointer',
                                    val > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
                                  )}
                                >
                                  {val > 0 ? formatCurrency(val) : '—'}
                                </button>
                              ) : (
                                <div className={cn(
                                  'h-7 flex items-center justify-end pr-5 text-right tabular-nums rounded',
                                  val > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
                                )}>
                                  {val > 0 ? formatCurrency(val) : '—'}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Ligne Réel */}
                      <tr className="border-t border-border/40 bg-muted/10">
                        <td className="px-3 py-2 text-muted-foreground font-medium bg-muted/20 border-r border-border sticky left-0 z-10 whitespace-nowrap">
                          Réel
                        </td>
                        {months.map((mi) => {
                          const rv = reelArr[mi] ?? null;
                          const prev = displayValues[mi] ?? 0;
                          const delta = rv !== null ? rv - prev : null;
                          return (
                            <td key={mi} className="px-1.5 py-1.5">
                              <div className="relative">
                                <input
                                  type="number"
                                  step="any"
                                  value={rv !== null ? String(rv) : ''}
                                  onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    handleReelChange(mi, raw === '' ? null : (parseFloat(raw) || 0));
                                  }}
                                  placeholder="—"
                                  className={cn(
                                    'h-7 w-full text-xs text-right pl-1 pr-5 border rounded focus:outline-none focus:ring-1 tabular-nums transition-colors',
                                    rv !== null
                                      ? delta !== null && delta <= 0
                                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700 font-medium focus:ring-emerald-400'
                                        : 'border-red-300 bg-red-50 text-red-700 font-medium focus:ring-red-400'
                                      : 'border-dashed border-gray-200 bg-transparent text-gray-400 placeholder:text-gray-300 focus:ring-ring',
                                  )}
                                />
                                <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">€</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
